import { AlertIconLight } from "@/assets/images/svgIcons/alertIcon"
import { Button } from "@/components/ui/baseUi/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/baseUi/command"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/baseUi/dialog"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useMySpaceNavigate } from "@/modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@/modules/navigation/routes"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import type { TemplateCategoryEntity } from "@/modules/template/entities/templateCategoryEntity"
import { templateFormValidation } from "@/modules/template/forms/templateFormValidation"
import type { EditExerciseTemplateInputDto } from "@/modules/template/gateway/templateDto"
import {
  useDeleteTemplate,
  useEditExerciseTemplate,
  useEditTemplate,
  useTemplate,
} from "@/modules/template/hooks/useTemplate"
import { useTemplateCategoriesInfinite } from "@/modules/template/hooks/useTemplateCategory"
import {
  useTemplateCategoriesSidebar,
  useTemplateSessionWrapperFormSidebar,
  useTemplateSingleBlockFormEditionSidebar,
} from "@/modules/template/hooks/useTemplateSidebar"
import { getTemplateFormDraft, useTemplateFormDraftActions } from "@/modules/template/stores/useTemplateFormDraftStore"
import { TemplateType } from "@/modules/template/types/templateType"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import { CheckIcon, ChevronsUpDownIcon, LoaderCircleIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react"
import { useImperativeHandle, useState } from "react"

export type TemplateFormRef = {
  onClose: () => Promise<boolean>
}
type TemplateFormProps = {
  templateId: string
  className?: string
  ref: React.Ref<TemplateFormRef>
}

type TemplateFormValues = {
  name: string
  description: string | null
  category: TemplateCategoryEntity | null
}

export function TemplateForm({ templateId, className, ref }: TemplateFormProps) {
  const { hideSidebar } = useSidebar()
  const confirmPopup = useConfirmPopup()
  const { navigateToTemplateCategories } = useTemplateCategoriesSidebar()
  const { navigateToTemplateSessionFormWrapper } = useTemplateSessionWrapperFormSidebar()
  const { navigateToTemplateSingleBlockFormEdition } = useTemplateSingleBlockFormEditionSidebar(templateId)
  const { mutateAsync: editExerciseTemplate } = useEditExerciseTemplate()
  const mySpaceNavigate = useMySpaceNavigate()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const { data: template } = useTemplate(templateId)
  const { mutateAsync: editTemplate } = useEditTemplate()
  const { mutateAsync: deleteTemplate, isPending: isDeletingTemplate } = useDeleteTemplate()
  const { setDraft, clearDraft } = useTemplateFormDraftActions()
  const [open, setOpen] = useState(false)
  const { data } = useTemplateCategoriesInfinite({
    searchTerm: undefined,
    page: 0,
    pageSize: 150,
  })

  const draft = getTemplateFormDraft(templateId)

  const defaultValues: TemplateFormValues = {
    name: draft?.name ?? template.name,
    description: draft?.description ?? template.description ?? "",
    category: draft?.category ?? template.templateCategory,
  }

  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
    },
    listeners: {
      onChange: ({ formApi }) => {
        setDraft(templateId, formApi.state.values)
      },
    },
    validators: {
      onSubmit: templateFormValidation,
      onBlur: templateFormValidation,
    },
    onSubmit: async ({ value }) => {
      await editTemplate({
        templateId: templateId,
        template: { name: value.name, description: value.description, templateCategoryId: value.category?.id ?? null },
      })
        .then(() => {
          clearDraft(templateId)
          toast.success(word("template.toast.updated.success"), {
            classNames: successToastClassName(),
          })
          hideSidebar(false)
        })
        .catch(() => {
          toast.error(word("template.toast.updated.error"), {
            classNames: errorToastClassName(),
          })
        })
    },
  })

  useImperativeHandle(ref, () => ({
    onClose: async () => {
      if (!form.state.isPristine) {
        return new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: word("global.form.closeMessage.title"),
            message: word("global.form.closeMessage.message"),
            accept: () => {
              resolve(true)
              return Promise.resolve()
            },
            cancel: () => {
              resolve(false)
              return Promise.resolve()
            },
            footerProps: {
              confirmText: word("global.ok"),
            },
          })
        })
      }

      clearDraft(templateId)
      return Promise.resolve(true)
    },
  }))

  const handleDeleteTemplate = async () => {
    await deleteTemplate(templateId)
      .then(() => {
        toast.success(word("template.toast.deleted.success"), {
          classNames: successToastClassName(),
        })
        hideSidebar(false)
        if (template.templateType === TemplateType.WEEK) {
          mySpaceNavigate(Path.MySpace.Templates)
        }
      })
      .catch(() => {
        toast.error(word("template.toast.deleted.error"), {
          classNames: errorToastClassName(),
        })
      })
  }

  let title: string
  switch (template.templateType) {
    case TemplateType.WEEK:
      title = word("template.title.text.weekTemplate")
      break
    case TemplateType.SESSION:
      title = word("template.title.text.sessionTemplate")
      break
    case TemplateType.EXERCISE:
      title = word("template.title.text.blockTemplate")
      break
    default: {
      const exhaustiveCheck: never = template.templateType
      throw new Error(`Invalid template type ${String(exhaustiveCheck)}`)
    }
  }

  let editTemplateButton: string
  switch (template.templateType) {
    case TemplateType.WEEK:
      editTemplateButton = word("template.button.text.editWeekTemplate")
      break
    case TemplateType.SESSION:
      editTemplateButton = word("template.button.text.editSessionTemplate")
      break
    case TemplateType.EXERCISE:
      editTemplateButton = word("template.button.text.editBlockTemplate")
      break
    default: {
      const exhaustiveCheck: never = template.templateType
      throw new Error(`Invalid template type ${String(exhaustiveCheck)}`)
    }
  }

  const editTemplateAction = () => {
    switch (template.templateType) {
      case TemplateType.WEEK:
        mySpaceNavigate(Path.MySpace.TemplateWeek(templateId))
        break
      case TemplateType.SESSION:
        navigateToTemplateSessionFormWrapper({ templateId })
        break
      case TemplateType.EXERCISE:
        navigateToTemplateSingleBlockFormEdition(async (exerciseInput: EditExerciseTemplateInputDto) => {
          await editExerciseTemplate({
            templateId: templateId,
            exerciseInput: exerciseInput,
          })
          hideSidebar(false)
        })
        break
      default: {
        const exhaustiveCheck: never = template.templateType
        throw new Error(`Invalid template type ${String(exhaustiveCheck)}`)
      }
    }
  }

  return (
    <div className={cn("flex flex-1 flex-col gap-4 px-6 pt-18 pb-6", className)}>
      <Text fontStyle="TL">{title}</Text>

      <div className="flex flex-1 flex-col justify-between">
        <div className="flex flex-col gap-5">
          <form.AppField name="name">
            {(field) => (
              <field.Field>
                <field.Label className="uppercase">{word("global.form.label.defaultName")}</field.Label>
                <field.Content>
                  <field.Input name="name" placeholder={word("template.form.name.placeholder")} />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="description">
            {(field) => (
              <field.Field>
                <field.Label className="uppercase">{word("global.form.label.shortDescription")}</field.Label>
                <field.Content>
                  <field.Textarea
                    name="description"
                    placeholder={word("template.form.description.placeholder")}
                    rows={4}
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <div className="flex flex-row items-end gap-2">
            <form.AppField name="category">
              {(field) => (
                <field.Field>
                  <field.Label className="uppercase">{word("global.form.label.shortDescription")}</field.Label>
                  <field.Content>
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger
                        render={
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="h-12 w-full justify-between"
                          >
                            {field.state.value?.name ?? word("global.form.label.category")}
                            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        }
                      />
                      <PopoverContent className="w-auto border-0 p-0" align="center">
                        <Command
                          className="**:data-[slot=command-input-wrapper]:bg-neutral-50"
                          filter={(value, search) => {
                            if (value.toLowerCase().includes(search.toLowerCase())) return 1
                            return 0
                          }}
                        >
                          <CommandInput placeholder={word("global.search.placeholder")} className="" />
                          <CommandList>
                            <CommandEmpty>{word("global.search.notFound")}</CommandEmpty>
                            <CommandGroup>
                              {data.map((category) => (
                                <CommandItem
                                  key={category.id}
                                  value={category.name}
                                  onSelect={() => {
                                    field.handleChange(category)
                                    setOpen(false)
                                  }}
                                  className="cursor-pointer hover:bg-neutral-200"
                                >
                                  <CheckIcon
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      field.state.value?.id === category.id ? "opacity-100" : "opacity-0",
                                    )}
                                  />
                                  {category.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
            <Button
              variant="outline"
              size="iconLg"
              onClick={() => {
                navigateToTemplateCategories()
              }}
            >
              <PlusIcon />
            </Button>
          </div>

          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              editTemplateAction()
            }}
          >
            <PencilIcon />
            {editTemplateButton}
          </Button>
        </div>

        <form.Subscribe selector={(state) => [state.isSubmitting, state.isPristine]}>
          {([isSubmitting, isPristine]) => (
            <div className="flex flex-row justify-end gap-2">
              <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogTrigger
                  render={
                    <Button
                      variant="outline"
                      size="lg"
                      className="flex-1"
                      disabled={isSubmitting || isDeletingTemplate}
                    >
                      <Trash2Icon />
                      {word("global.delete")}
                    </Button>
                  }
                />
                <DialogContent className="max-w-lg gap-6 p-8">
                  <DialogHeader className="flex flex-col items-center justify-center gap-6 pt-4 text-center">
                    <DialogTitle className="flex flex-col items-center justify-center gap-1 title-large!">
                      <AlertIconLight color="currentColor" />
                      {word("template.dialog.delete.title")}
                    </DialogTitle>
                    <DialogDescription className="body-medium!">
                      {word("template.dialog.delete.description")}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      size="lg"
                      className="flex-1"
                      onClick={() => {
                        setIsDeleteDialogOpen(false)
                      }}
                      disabled={isDeletingTemplate}
                    >
                      {word("global.cancel")}
                    </Button>
                    <Button
                      variant="negativePrimary"
                      size="lg"
                      className="flex-1"
                      onClick={() => {
                        void handleDeleteTemplate()
                      }}
                      disabled={isDeletingTemplate}
                    >
                      {isDeletingTemplate ? (
                        <LoadingSpinner className="animate-spin" size={16} />
                      ) : (
                        word("global.delete")
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Button
                variant="primary"
                size="lg"
                className="flex-1"
                onClick={() => void form.handleSubmit()}
                disabled={isSubmitting || isPristine}
              >
                {form.state.isSubmitting && <LoaderCircleIcon className="animate-spin" />}
                {word("global.save")}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </div>
    </div>
  )
}
